/**
 * Pure business logic extracted from RecordPaymentModal.
 *
 * These functions are stateless and testable without React or DOM dependencies.
 */

import { toSmallestUnit, fromSmallestUnit, getCurrencyConfig } from '../../utils/formatters'

import type { Account } from '../../services/account-service'
import type { BankAccount } from '../../services/bank-account-service'
import type { PaymentAllocationInput } from '../../services/payment-service'
import type { Invoice } from '../../types/billing'

// ============================================================================
// Types
// ============================================================================

export interface PreviewLine {
  type: 'debit' | 'credit'
  accountCode: string
  accountName: string
  currency: string
  amount: number       // display units (not smallest)
  baseAmount: number   // base currency display units
  memo: string
}

export interface PreviewContext {
  paymentAmount: number
  advanceApplied: number
  isForeignCurrency: boolean
  paymentExchangeRate: string
  baseCurrency: string
  invoiceCurrency: string
  intermediaryFee: string
  destinationFee: string
  hasSeparateIntermediaryFee: boolean
  selectedBankAccountId: string
  bankAccounts: BankAccount[]
  cashBankGLAccounts: Account[]
  feeAccountId: string
  expenseAccounts: Account[]
  allocatedAmount: number
  unallocatedAmount: number
  fxImpact: number
  receivableAccountCode: string
  receivableAccountName: string
  advanceAccountCode: string
  advanceAccountName: string
  shouldWriteOffRemaining: boolean
  remainingAfterPayment: number
  tdsAmount: string
  tdsAccountId: string
  tdsAccounts: Account[]
  invoiceType: string
  partyName: string
  isVendorBill: boolean
}

// ============================================================================
// Helpers
// ============================================================================

export function localDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function computeAllocation(
  paymentAmount: number,
  advanceApplied: number,
  outstandingBalance: number,
): { allocatedAmount: number; unallocatedAmount: number; remainingAfterPayment: number } {
  const totalSettlement = paymentAmount + advanceApplied
  const allocatedAmount = Math.min(totalSettlement, outstandingBalance)
  const unallocatedAmount = Math.max(0, totalSettlement - allocatedAmount)
  const remainingAfterPayment = outstandingBalance - allocatedAmount
  return { allocatedAmount, unallocatedAmount, remainingAfterPayment }
}

export function shouldShowWriteOff(remainingAfterPayment: number): boolean {
  return remainingAfterPayment > 0 && remainingAfterPayment <= 10
}

export interface ComputeFxImpactParams {
  isForeignCurrency: boolean
  paymentExchangeRate: string
  invoiceExchangeRate: string
  allocatedAmount: number
  invoiceCurrency: string
}

export function computeFxImpact({
  isForeignCurrency,
  paymentExchangeRate,
  invoiceExchangeRate,
  allocatedAmount,
  invoiceCurrency,
}: ComputeFxImpactParams): number {
  if (!isForeignCurrency || !paymentExchangeRate) { return 0 }
  const pmtRate = parseFloat(paymentExchangeRate)
  const invRate = parseFloat(invoiceExchangeRate || '0')
  if (!pmtRate || !invRate || pmtRate <= 0 || invRate <= 0) { return 0 }

  const allocSmallest = toSmallestUnit(allocatedAmount, invoiceCurrency)
  const bookValue = allocSmallest * invRate
  const marketValue = allocSmallest * pmtRate
  return Math.round(bookValue - marketValue)
}

export function buildPaymentAccountOptions(
  paymentMethod: string,
  bankAccounts: BankAccount[],
  cashBankGLAccounts: Account[],
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = []

  let matchedBank: BankAccount[]
  if (paymentMethod === 'PAYMENT_METHOD_CASH') {
    matchedBank = bankAccounts.filter(acc => acc.accountType === 'cash')
  } else if (paymentMethod === 'PAYMENT_METHOD_CREDIT_CARD') {
    matchedBank = bankAccounts.filter(acc => acc.accountType === 'cc')
  } else {
    matchedBank = bankAccounts.filter(acc => acc.accountType === 'current' || acc.accountType === 'savings' || acc.accountType === 'od')
  }
  const bankList = matchedBank.length > 0 ? matchedBank : bankAccounts
  const bankLinkedAccountIds = new Set(bankAccounts.map(b => b.accountId))

  for (const acc of bankList) {
    options.push({
      value: `bank:${acc.id}`,
      label: `${acc.accountName}${acc.maskedAccountNumber ? ` (${acc.maskedAccountNumber})` : ''}`,
    })
  }
  for (const gl of cashBankGLAccounts) {
    if (bankLinkedAccountIds.has(gl.id)) { continue }
    options.push({ value: `gl:${gl.id}`, label: `${gl.code} — ${gl.name}` })
  }
  return options
}

function resolveBankAccount(
  selectedBankAccountId: string,
  bankAccounts: BankAccount[],
  cashBankGLAccounts: Account[],
): { code: string; name: string } {
  if (selectedBankAccountId.startsWith('bank:')) {
    const ba = bankAccounts.find(b => `bank:${b.id}` === selectedBankAccountId)
    if (ba) {
      const gl = cashBankGLAccounts.find(g => g.id === ba.accountId)
      return { code: gl?.code ?? ba.accountName, name: ba.accountName }
    }
  } else if (selectedBankAccountId.startsWith('gl:')) {
    const gl = cashBankGLAccounts.find(g => `gl:${g.id}` === selectedBankAccountId)
    if (gl) { return { code: gl.code, name: gl.name } }
  }
  return { code: '????', name: 'Bank Account' }
}

function resolveFeeAccount(
  feeAccountId: string,
  expenseAccounts: Account[],
): { code: string; name: string } {
  if (feeAccountId) {
    const fa = expenseAccounts.find(a => a.id === feeAccountId)
    if (fa) { return { code: fa.code, name: fa.name } }
  }
  return { code: '????', name: 'Fee Expense' }
}

function computeBankDrAmt(netCashAmt: number, iFee: number, hasSeparateIntermediaryFee: boolean): number {
  if (iFee > 0 && hasSeparateIntermediaryFee) { return netCashAmt }
  if (iFee > 0) { return netCashAmt - iFee }
  return netCashAmt
}

function resolveAdvanceAccount(
  ctx: PreviewContext,
): { advCode: string; advName: string; partyName: string } {
  const partyName = ctx.partyName || (ctx.isVendorBill ? 'Vendor' : 'Customer')
  const defaultAdvCode = ctx.isVendorBill ? '1650' : '2760'
  const defaultAdvLabel = ctx.isVendorBill ? 'Vendor Advance' : 'Customer Advance'
  const advCode = ctx.advanceAccountCode !== defaultAdvCode ? ctx.advanceAccountCode : `${defaultAdvCode}-*`
  const advName = ctx.advanceAccountCode !== defaultAdvCode ? ctx.advanceAccountName : `${defaultAdvLabel} — ${partyName}`
  return { advCode, advName, partyName }
}

interface IntermediaryFeeLineParams {
  iFee: number
  ctx: PreviewContext
  bankCode: string
  bankName: string
  feeCode: string
  feeName: string
  pmtRate: number
}

function addIntermediaryFeeLines(lines: PreviewLine[], p: IntermediaryFeeLineParams): void {
  if (p.iFee <= 0) {return}
  if (p.ctx.hasSeparateIntermediaryFee) {
    lines.push({ type: 'credit', accountCode: p.bankCode, accountName: p.bankName, currency: p.ctx.invoiceCurrency, amount: p.iFee, baseAmount: p.iFee * p.pmtRate, memo: 'Intermediary bank fee' })
  }
  lines.push({ type: 'debit', accountCode: p.feeCode, accountName: p.feeName, currency: p.ctx.invoiceCurrency, amount: p.iFee, baseAmount: p.iFee * p.pmtRate, memo: 'Intermediary bank fee' })
}

interface DestinationFeeLineParams {
  dFee: number
  ctx: PreviewContext
  bankCode: string
  bankName: string
  feeCode: string
  feeName: string
  pmtRate: number
}

function addDestinationFeeLines(lines: PreviewLine[], p: DestinationFeeLineParams): void {
  if (p.dFee <= 0) {return}
  if (p.ctx.isForeignCurrency && p.pmtRate > 0) {
    const feeInPaymentCurrency = p.dFee / p.pmtRate
    const feeBase = feeInPaymentCurrency * p.pmtRate
    lines.push({ type: 'credit', accountCode: p.bankCode, accountName: p.bankName, currency: p.ctx.invoiceCurrency, amount: feeInPaymentCurrency, baseAmount: feeBase, memo: 'Destination bank fee' })
    lines.push({ type: 'debit', accountCode: p.feeCode, accountName: p.feeName, currency: p.ctx.invoiceCurrency, amount: feeInPaymentCurrency, baseAmount: feeBase, memo: 'Destination bank fee' })
    return
  }
  lines.push({ type: 'credit', accountCode: p.bankCode, accountName: p.bankName, currency: p.ctx.baseCurrency, amount: p.dFee, baseAmount: p.dFee, memo: 'Destination bank fee' })
  lines.push({ type: 'debit', accountCode: p.feeCode, accountName: p.feeName, currency: p.ctx.baseCurrency, amount: p.dFee, baseAmount: p.dFee, memo: 'Destination bank fee' })
}

function addBankLines(ctx: PreviewContext, lines: PreviewLine[], pmtRate: number, partyName: string): void {
  const iFee = parseFloat(ctx.intermediaryFee) || 0
  const dFee = parseFloat(ctx.destinationFee) || 0
  const tdsAmt = parseFloat(ctx.tdsAmount) || 0
  const netCashAmt = ctx.paymentAmount - tdsAmt
  if (netCashAmt <= 0) {return}

  const { code: bankCode, name: bankName } = resolveBankAccount(ctx.selectedBankAccountId, ctx.bankAccounts, ctx.cashBankGLAccounts)
  const { code: feeCode, name: feeName } = resolveFeeAccount(ctx.feeAccountId, ctx.expenseAccounts)

  const bankDrAmt = computeBankDrAmt(netCashAmt, iFee, ctx.hasSeparateIntermediaryFee)
  const bankLineType = ctx.isVendorBill ? 'credit' : 'debit'
  const bankMemo = ctx.isVendorBill ? `Payment to ${partyName}` : `Payment received from ${partyName}`
  lines.push({ type: bankLineType, accountCode: bankCode, accountName: bankName, currency: ctx.invoiceCurrency, amount: bankDrAmt, baseAmount: bankDrAmt * pmtRate, memo: bankMemo })

  addIntermediaryFeeLines(lines, { iFee, ctx, bankCode, bankName, feeCode, feeName, pmtRate })
  addDestinationFeeLines(lines, { dFee, ctx, bankCode, bankName, feeCode, feeName, pmtRate })
}

// eslint-disable-next-line max-params -- TODO(refactor): convert to options object
function addAdvanceLine(ctx: PreviewContext, lines: PreviewLine[], pmtRate: number, advCode: string, advName: string, partyName: string): void {
  if (ctx.advanceApplied <= 0) { return }
  const advLineType = ctx.isVendorBill ? 'credit' : 'debit'
  lines.push({ type: advLineType, accountCode: advCode, accountName: advName, currency: ctx.invoiceCurrency, amount: ctx.advanceApplied, baseAmount: ctx.advanceApplied * pmtRate, memo: `Advance set-off — ${partyName}` })
}

function addSettlementLine(ctx: PreviewContext, lines: PreviewLine[], pmtRate: number, partyName: string): void {
  if (ctx.allocatedAmount <= 0) { return }
  const settlementLineType = ctx.isVendorBill ? 'debit' : 'credit'
  const baseAmount = ctx.isForeignCurrency && ctx.fxImpact !== 0
    ? ctx.allocatedAmount * pmtRate + fromSmallestUnit(ctx.fxImpact, ctx.baseCurrency)
    : ctx.allocatedAmount * pmtRate
  lines.push({ type: settlementLineType, accountCode: ctx.receivableAccountCode, accountName: ctx.receivableAccountName, currency: ctx.invoiceCurrency, amount: ctx.allocatedAmount, baseAmount, memo: `Invoice settlement — ${partyName}` })
}

function addTdsLine(ctx: PreviewContext, lines: PreviewLine[], pmtRate: number, partyName: string): void {
  const tdsAmt = parseFloat(ctx.tdsAmount) || 0
  if (tdsAmt <= 0 || !ctx.tdsAccountId) { return }
  const tdsAcc = ctx.tdsAccounts.find(a => a.id === ctx.tdsAccountId)
  const tdsCode = tdsAcc?.code ?? '????'
  const tdsName = tdsAcc?.name ?? 'TDS'
  const isVendorInvoice = ctx.invoiceType === 'vendor_bill' || ctx.invoiceType === 'vendor_credit_note'
  const tdsType = isVendorInvoice ? 'credit' : 'debit'
  const tdsMemo = isVendorInvoice ? `TDS deducted — ${partyName}` : `TDS deducted by ${partyName}`
  lines.push({ type: tdsType, accountCode: tdsCode, accountName: tdsName, currency: ctx.invoiceCurrency, amount: tdsAmt, baseAmount: tdsAmt * pmtRate, memo: tdsMemo })
}

function addWriteOffLines(ctx: PreviewContext, lines: PreviewLine[], pmtRate: number): void {
  if (!ctx.shouldWriteOffRemaining || ctx.remainingAfterPayment <= 0) { return }
  const woAmt = ctx.remainingAfterPayment
  const woDebit = ctx.isVendorBill ? ctx.receivableAccountCode : '9910'
  const woDebitName = ctx.isVendorBill ? ctx.receivableAccountName : 'Rounding Off'
  const woCredit = ctx.isVendorBill ? '9910' : ctx.receivableAccountCode
  const woCreditName = ctx.isVendorBill ? 'Rounding Off' : ctx.receivableAccountName
  lines.push({ type: 'debit', accountCode: woDebit, accountName: woDebitName, currency: ctx.invoiceCurrency, amount: woAmt, baseAmount: woAmt * pmtRate, memo: 'Write-off rounding difference' })
  lines.push({ type: 'credit', accountCode: woCredit, accountName: woCreditName, currency: ctx.invoiceCurrency, amount: woAmt, baseAmount: woAmt * pmtRate, memo: 'Write-off rounding difference' })
}

function addFxLines(ctx: PreviewContext, lines: PreviewLine[], partyName: string): void {
  if (ctx.fxImpact > 0) {
    lines.push({ type: 'debit', accountCode: '9900', accountName: 'Foreign Exchange Loss', currency: ctx.baseCurrency, amount: fromSmallestUnit(ctx.fxImpact, ctx.baseCurrency), baseAmount: fromSmallestUnit(ctx.fxImpact, ctx.baseCurrency), memo: `Realized FX loss — ${partyName}` })
  } else if (ctx.fxImpact < 0) {
    lines.push({ type: 'credit', accountCode: '4950', accountName: 'Foreign Exchange Gain', currency: ctx.baseCurrency, amount: fromSmallestUnit(-ctx.fxImpact, ctx.baseCurrency), baseAmount: fromSmallestUnit(-ctx.fxImpact, ctx.baseCurrency), memo: `Realized FX gain — ${partyName}` })
  }
}

export function buildPreviewLines(ctx: PreviewContext): PreviewLine[] {
  if (ctx.paymentAmount <= 0 && ctx.advanceApplied <= 0) { return [] }
  const pmtRate = ctx.isForeignCurrency && ctx.paymentExchangeRate ? parseFloat(ctx.paymentExchangeRate) : 1
  if (!pmtRate || pmtRate <= 0) { return [] }

  const lines: PreviewLine[] = []
  const { advCode, advName, partyName } = resolveAdvanceAccount(ctx)

  addBankLines(ctx, lines, pmtRate, partyName)
  addAdvanceLine(ctx, lines, pmtRate, advCode, advName, partyName)
  addSettlementLine(ctx, lines, pmtRate, partyName)
  addTdsLine(ctx, lines, pmtRate, partyName)

  if (ctx.unallocatedAmount > 0) {
    const unallocLineType = ctx.isVendorBill ? 'debit' : 'credit'
    lines.push({ type: unallocLineType, accountCode: advCode, accountName: advName, currency: ctx.invoiceCurrency, amount: ctx.unallocatedAmount, baseAmount: ctx.unallocatedAmount * pmtRate, memo: `Advance payment — ${partyName}` })
  }

  addWriteOffLines(ctx, lines, pmtRate)
  addFxLines(ctx, lines, partyName)

  return lines
}

export function computePreviewBalance(lines: PreviewLine[]): { totalDebit: number; totalCredit: number; isBalanced: boolean } {
  const totalDebit = lines.filter(l => l.type === 'debit').reduce((s, l) => s + l.baseAmount, 0)
  const totalCredit = lines.filter(l => l.type === 'credit').reduce((s, l) => s + l.baseAmount, 0)
  return { totalDebit, totalCredit, isBalanced: Math.abs(totalDebit - totalCredit) < 0.01 }
}

// ============================================================================
// Payment request builder
// ============================================================================

export interface BuildPaymentRequestArgs {
  paymentAmount: number
  advanceApplied: number
  isVendorBill: boolean
  partyId: string
  partyName: string
  paymentDate: Date
  paymentMethod: string
  paymentRef: string
  selectedBankAccountId: string
  invoiceCurrency: string
  baseCurrency: string
  isForeignCurrency: boolean
  paymentExchangeRate: string
  invoiceId: string
  allocatedAmount: number
  paymentNotes: string
  intermediaryFee: string
  hasSeparateIntermediaryFee: boolean
  destinationFee: string
  hasSeparateDestinationFee: boolean
  feeAccountId: string
  tdsAmount: string
  tdsAccountId: string
}

export type PaymentRequestPayload = {
  direction: 'inward' | 'outward'
  partyId: string
  partyName: string
  paymentDate: string
  paymentAmount: number
  paymentMethod: string
  referenceNumber?: string
  bankAccountId?: string
  currency: string
  exchangeRate?: string
  allocations: { invoiceId: string; allocatedAmount: number }[]
  notes?: string
  intermediaryFeeAmount?: number
  separateIntermediaryFee?: boolean
  destinationFeeAmount?: number
  separateDestinationFee?: boolean
  feeAccountId?: string
  advanceAppliedAmount?: number
  tdsAmount?: number
  tdsAccountId?: string
}

function buildFeeFields(args: BuildPaymentRequestArgs, iFee: number, dFee: number): Pick<PaymentRequestPayload, 'intermediaryFeeAmount' | 'separateIntermediaryFee' | 'destinationFeeAmount' | 'separateDestinationFee' | 'feeAccountId'> {
  return {
    intermediaryFeeAmount: iFee > 0 ? toSmallestUnit(iFee, args.invoiceCurrency) : undefined,
    separateIntermediaryFee: iFee > 0 ? args.hasSeparateIntermediaryFee : undefined,
    destinationFeeAmount: dFee > 0 ? toSmallestUnit(dFee, args.baseCurrency) : undefined,
    separateDestinationFee: dFee > 0 ? args.hasSeparateDestinationFee : undefined,
    feeAccountId: (iFee > 0 || dFee > 0) && args.feeAccountId ? args.feeAccountId : undefined,
  }
}

function extractBankAccountId(args: BuildPaymentRequestArgs): string | undefined {
  return args.paymentAmount > 0 && args.selectedBankAccountId.startsWith('bank:')
    ? args.selectedBankAccountId.slice(5)
    : undefined
}

function buildTdsAndAdvanceFields(args: BuildPaymentRequestArgs):
Pick<PaymentRequestPayload, 'advanceAppliedAmount' | 'tdsAmount' | 'tdsAccountId'> {
  const adv = args.advanceApplied
  const tds = parseFloat(args.tdsAmount)
  return {
    advanceAppliedAmount: adv > 0 ? toSmallestUnit(adv, args.invoiceCurrency) : undefined,
    tdsAmount: tds > 0 ? toSmallestUnit(tds, args.invoiceCurrency) : undefined,
    tdsAccountId: tds > 0 && args.tdsAccountId ? args.tdsAccountId : undefined,
  }
}

export function buildPaymentRequest(args: BuildPaymentRequestArgs): PaymentRequestPayload {
  const iFee = parseFloat(args.intermediaryFee)
  const dFee = parseFloat(args.destinationFee)

  return {
    direction: args.isVendorBill ? 'outward' : 'inward',
    partyId: args.partyId,
    partyName: args.partyName,
    paymentDate: localDateString(args.paymentDate),
    paymentAmount: toSmallestUnit(args.paymentAmount, args.invoiceCurrency),
    paymentMethod: args.paymentMethod,
    referenceNumber: args.paymentRef || undefined,
    bankAccountId: extractBankAccountId(args),
    currency: args.invoiceCurrency,
    exchangeRate: args.isForeignCurrency && args.paymentExchangeRate ? args.paymentExchangeRate : undefined,
    allocations: [{ invoiceId: args.invoiceId, allocatedAmount: toSmallestUnit(args.allocatedAmount, args.invoiceCurrency) }],
    notes: args.paymentNotes || undefined,
    ...buildFeeFields(args, iFee, dFee),
    ...buildTdsAndAdvanceFields(args),
  }
}

// ============================================================================
// FIFO allocation (shared between RecordPayment and ReconAddEntry flows)
// ============================================================================

/**
 * Distribute a payment amount across unpaid invoices in chronological order.
 * Returns allocations in display units (not smallest unit).
 */
export function computeFifoAllocations(
  paymentAmount: number | null,
  unpaidInvoices: Invoice[],
  paymentCurrency: string,
): PaymentAllocationInput[] {
  const amt = paymentAmount ?? 0
  if (amt <= 0) { return [] }
  let remaining = amt
  const allocs: PaymentAllocationInput[] = []
  for (const inv of unpaidInvoices) {
    if (remaining <= 0) { break }
    const currency = inv.currency ?? paymentCurrency
    const outstandingSmallest = (inv.totalAmount ?? 0) - (inv.paymentsReceived ?? 0)
    if (outstandingSmallest <= 0) { continue }
    const outstanding = fromSmallestUnit(outstandingSmallest, currency)
    const allocated = Math.min(remaining, outstanding)
    const decimals = getCurrencyConfig(currency).decimalPlaces
    const factor = Math.pow(10, decimals)
    allocs.push({ invoiceId: inv.id, allocatedAmount: Math.round(allocated * factor) / factor })
    remaining -= allocated
  }
  return allocs
}
